<script setup lang="ts">
import type { ProgressBarSegment } from '~/features/my-wallet/types/charts'

interface Props {
  segments: ProgressBarSegment[]
  markers?: number[]
  size?: 'small' | 'large'
}

const props = withDefaults(defineProps<Props>(), {
  markers: () => [],
  size: 'small',
})

function clampToTrack(position: number) {
  return Math.max(0, Math.min(100, position))
}

const visibleSegments = computed(() => props.segments
  .map(segment => ({ ...segment, from: clampToTrack(segment.from), to: clampToTrack(segment.to) }))
  .filter(segment => segment.to > segment.from))
</script>

<template>
  <div class="progress-bar" :class="`-${size}`" aria-hidden="true">
    <span
      v-for="(segment, index) in visibleSegments"
      :key="`${segment.tone}-${index}`"
      class="progress-bar-segment"
      :class="`-${segment.tone}`"
      :style="{ left: `${segment.from}%`, width: `${segment.to - segment.from}%` }"
    />
    <span
      v-for="marker in markers"
      :key="`marker-${marker}`"
      class="progress-bar-marker"
      :style="{ left: `${clampToTrack(marker)}%` }"
    />
  </div>
</template>

<style scoped lang="scss">
@use '../styles/model-tones' as tones;

.progress-bar {
  @apply relative w-full overflow-hidden bg-neutral-high-medium;

  &.-small {
    @apply h-[10px] rounded-md;

    .progress-bar-marker {
      @apply w-[2px] bg-neutral-high-dark;
    }
  }

  &.-large {
    @apply h-6 rounded-md;

    .progress-bar-marker {
      @apply w-px bg-neutral-high-pure bg-opacity-60;
    }
  }

  .progress-bar-segment {
    @apply absolute inset-y-0;
    @include tones.tone-modifiers(bg, ('model-c', 'model-b', 'model-a', 'muted', 'waived', 'locked'));
  }

  .progress-bar-marker {
    @apply absolute inset-y-0;
  }
}
</style>
